import { useIsLoggedInRetailer } from "@faire/deprecated-retailer-visitor-shared/lib/isLoggedInRetailer";
import {
  getNavigationTreeHashCode,
  NAVIGATION_TREE_HASH_CODE_PROPERTY_NAME,
} from "@faire/deprecated-retailer-visitor-shared/serialized-data/getNavigationTreeHashCode";
import { useCategoryStore } from "@faire/deprecated-retailer-visitor-shared/stores/domain/useCategoryStore";
import { useInvalidateLoggedInNavigationQueries } from "@faire/deprecated-retailer-visitor-shared/stores/domain/useNavigationQuery";
import { isNotUndefined } from "@faire/typescript";
import { useMutation as useRecordCategoryNodesVisibilityMutation } from "@faire/web-api--source/endpoints/www/api/category/record-category-nodes-visibility/post-hooks";
import { IRecordCategoryNodesVisibilityRequest } from "@faire/web-api--source/indigofair/category/IRecordCategoryNodesVisibilityRequest";
import { overrideGlobalProperty } from "@faire/web/common/globals/override";
import isChromatic from "chromatic/isChromatic";
import { useCallback } from "react";

import Source = IRecordCategoryNodesVisibilityRequest.Source;

let lastSeenCategoryPathIdentifiersString: string | undefined = undefined;
// We only want to override the tree version once so we don't thrash
let hasOverridenTreeVersionThisSession: boolean = false;

export const useMaybeAssignCategoryTreeExperiment = () => {
  "use no memo";
  const isLoggedInRetailer = useIsLoggedInRetailer();
  const { invalidateNavigationQueries } =
    useInvalidateLoggedInNavigationQueries();
  const { refetchCategories } = useCategoryStore();
  const { mutateAsync: fetchRecordCategoryNodesVisibility } =
    useRecordCategoryNodesVisibilityMutation();

  return useCallback(
    async (
      getCategoryPathIdentifiers: () => (string | undefined)[],
      source: keyof typeof Source = Source.SOURCE_UNKNOWN
    ) => {
      if (!isLoggedInRetailer || isChromatic()) {
        return;
      }

      // Early exit if empty or last call was the same as this one, debounce.
      const categoryPathIdentifiers =
        getCategoryPathIdentifiers().filter(isNotUndefined);
      if (categoryPathIdentifiers.length === 0) {
        return;
      }

      const categoryPathIdentifiersString = JSON.stringify(
        categoryPathIdentifiers
      );

      if (
        categoryPathIdentifiersString === lastSeenCategoryPathIdentifiersString
      ) {
        return;
      }

      lastSeenCategoryPathIdentifiersString = categoryPathIdentifiersString;

      // Deal with seasonal and category filter keys prefixes by stripping then and
      // retaining only the actual category path identifier.
      // Also added filter for nav_link - removing prefixes for mobile nav section
      const categoryPathIdentifiersWithoutPrefix = categoryPathIdentifiers.map(
        (pathIdentifier) =>
          pathIdentifier.replace(/^(category:|\/nav_link\/)|/, "")
      );

      // Invoke the backend that has the context on the A/B test.
      try {
        const response = await fetchRecordCategoryNodesVisibility([
          {
            path_identifiers: categoryPathIdentifiersWithoutPrefix,
            source,
          },
        ]);

        const navigationTreeHashcode = getNavigationTreeHashCode();

        // If we have both a serialized category tree and a backend response with a hashcode (or tree type [legacy]) that don't match
        // We should refetch our category and navigation trees so we don't have a mismatch between the trees that the BE believes we should have
        // and what the FE actually has. If we don't do this, we can end up with cached trees that differ from what we need
        const globalTreeVersion = navigationTreeHashcode;
        const newTreeVersionValue = response.navigation_tree_hash_code;

        if (
          !!globalTreeVersion &&
          !!newTreeVersionValue &&
          newTreeVersionValue !== globalTreeVersion &&
          !hasOverridenTreeVersionThisSession
        ) {
          // Only do this once per session because the backend can end up in a race condition on settings where it gives us different results
          hasOverridenTreeVersionThisSession = true;

          overrideGlobalProperty(
            NAVIGATION_TREE_HASH_CODE_PROPERTY_NAME,
            newTreeVersionValue
          );
          refetchCategories();
          invalidateNavigationQueries();
        }
      } catch {
        // Swallow the error
      }
    },
    [
      isLoggedInRetailer,
      fetchRecordCategoryNodesVisibility,
      refetchCategories,
      invalidateNavigationQueries,
    ]
  );
};

/**
 * This function is only for TESTING purposes where we want to
 * reset all the values from previous tests. Should be used in
 * beforeEach/afterEach blocks. DO NOT USE ANYWHERE ELSE.
 */
export const resetCurrentSessionValuesForTest = () => {
  lastSeenCategoryPathIdentifiersString = undefined;
  hasOverridenTreeVersionThisSession = false;
};
